import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, OrderLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@store/theme/design";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    location: getLocation(state),
});

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel | ProductModel;
    extendedStyles?: ProductPromotionTextBannerStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPromotionTextBannerStyles {
    label?: TypographyProps;
    wrapper?: InjectableCss;
}

export const ProductPromotionTextBannerStyles: ProductPromotionTextBannerStyles = {
    wrapper: {
        css: css`
            position: absolute;
            left: 0;
            z-index: 1;
            padding: 1em 2.35em;
            min-width: 150px;
            text-align: center;
        `,
    },
    label: {
        size: TYPOGRAPHY.size.smaller,
        color: "white",
        css: css`
            font-weight: bold;
            text-transform: uppercase;
            margin-bottom: 2em;
        `,
    },
};

const ProductPromotionTextBanner: FC<Props> = ({ product, extendedStyles, location }) => {
    const [styles] = React.useState(() => mergeToNew(ProductPromotionTextBannerStyles, extendedStyles));
    let message = "";
    let bannerColor = "";
    const productData = "product" in product ? product.product : product; // manage case when the component receives a productContext
    if (!productData || !productData.properties) {
        return null;
    }

    const isPromo = productData.properties.isPromotion;
    const isNew = productData.properties.isNewProduct;
    const isClearance = productData.properties.isClearance;

    if (!isPromo && !isNew && !isClearance) {
        return null;
    }

    const promotionCategoriesUrls = [translate("/catalog/products/promotions").toLowerCase()];
    const newProductsCategoriesUrls = [translate("/catalog/products/new-products").toLowerCase()];
    const clearanceCategoriesUrls = [translate("/catalog/products/clearance").toLowerCase()];

    if (isPromo && isPromo.toLowerCase() === "true") {
        if (!promotionCategoriesUrls.includes(location.pathname.toLowerCase())) {
            message = translate("productImageBannerPromotion");
            bannerColor = COLORS.promotion;
        }
    }

    if (isNew && isNew.toLowerCase() === "true") {
        if (!newProductsCategoriesUrls.includes(location.pathname.toLowerCase())) {
            message = translate("productImageBannerNew");
            bannerColor = "black";
        }
    }

    if (isClearance && isClearance.toLocaleLowerCase() === "true") {
        if (!clearanceCategoriesUrls.includes(location.pathname.toLowerCase())) {
            message = translate("productImageBannerClearance");
            bannerColor = COLORS.promotion;
        }
    }

    const wrapperStylesToApply: InjectableCss = {
        css: css`
            ${styles.wrapper?.css}
            background: ${bannerColor};
        `,
    };

    if (message === "") {
        return null;
    }

    return (
        <StyledWrapper {...wrapperStylesToApply}>
            <Typography {...styles.label}>{message}</Typography>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductPromotionTextBanner);
